import { Button, Checkbox } from "@blueprintjs/core"
import classNames from "classnames"
import { Fuel } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

type FuelStationsSwitchProps = {
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  categories?: string[]
  visibleCategories?: string[]
  onVisibleCategoriesChange?: (visibleCategories: string[]) => void
  /** Placement; the default is the old fixed spot for maps without a column */
  className?: string
}

const FuelStationsSwitch = ({
  checked,
  onCheckedChange,
  categories = [],
  visibleCategories = [],
  onVisibleCategoriesChange,
  className = "absolute left-[10px] top-[115px] z-10",
}: FuelStationsSwitchProps) => {
  const { t } = useTranslation()
  // With categories the button opens a layer picker and stations follow the
  // selected categories; without them it directly toggles the stations.
  const hasCategories = categories.length > 0
  const [isPanelOpen, setIsPanelOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isPanelOpen) {
      return
    }

    const handleOutsideClick = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsPanelOpen(false)
      }
    }

    document.addEventListener("mousedown", handleOutsideClick)
    return () => document.removeEventListener("mousedown", handleOutsideClick)
  }, [isPanelOpen])

  const toggleCategory = (category: string) => {
    onVisibleCategoriesChange?.(
      visibleCategories.includes(category)
        ? visibleCategories.filter((visible) => visible !== category)
        : [...visibleCategories, category],
    )
  }

  const isActive = hasCategories
    ? isPanelOpen || visibleCategories.length > 0
    : checked

  return (
    <div ref={containerRef} className={className}>
      <Button
        id="toggle-fuel-stations"
        active={isActive}
        className={classNames(
          "!h-[30px] !min-h-[30px] !w-[30px] !min-w-[30px] !p-0 dark:text-white",
          isActive
            ? "!bg-[#14ae5c] !text-white hover:!bg-[#129651] dark:!bg-[#14ae5c]"
            : "!text-[#5f6b7c] hover:!text-[#5f6b7c] dark:bg-gray-700 dark:!text-white",
        )}
        icon={<Fuel size={16} strokeWidth={2.25} />}
        onClick={() =>
          hasCategories
            ? setIsPanelOpen(!isPanelOpen)
            : onCheckedChange(!checked)
        }
        title={
          hasCategories
            ? t("maps.fuelStationsSwitch.chooseLayers")
            : checked
              ? t("maps.fuelStationsSwitch.hide")
              : t("maps.fuelStationsSwitch.show")
        }
      />
      {isPanelOpen && hasCategories && (
        <div className="absolute left-[36px] top-0 w-max rounded bg-white p-2 shadow-md dark:bg-gray-700 dark:text-white">
          {categories.map((category) => (
            <Checkbox
              key={category}
              className="!mb-1 last:!mb-0"
              checked={visibleCategories.includes(category)}
              label={category}
              onChange={() => toggleCategory(category)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default FuelStationsSwitch
